import { ApiDashboardElement, ApiReturnMfe } from '@highimpact-frontend/api-types';
import { widgetCreationInfo } from './useWidgetCreationInfo';

export type BuiltinWidgetGroupId = 'table' | 'text' | 'image';

export interface BuiltinWidgetGroupConfig {
  label: string;
  formTitle: string;
  typeSelectorLabel: string;
  members: Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >[];
}

export const BUILTIN_WIDGET_GROUPS: Record<
  BuiltinWidgetGroupId,
  BuiltinWidgetGroupConfig
> = {
  table: {
    label: 'Tableau',
    formTitle: 'Ajouter un tableau',
    typeSelectorLabel: 'Type de tableau',
    members: ['table'],
  },
  text: {
    label: 'Text',
    formTitle: 'Ajouter un élément texte',
    typeSelectorLabel: 'Type de texte',
    members: ['text'],
  },
  image: {
    label: 'Image',
    formTitle: 'Ajouter un élément image',
    typeSelectorLabel: "Type d'image",
    members: ['image', 'imagegallery'],
  },
};

const GROUPED_BUILTIN_KEYS = new Set<string>(
  Object.values(BUILTIN_WIDGET_GROUPS).flatMap((group) => group.members)
);

export type WidgetDropdownItem = {
  value: string;
  label: string;
  kind: 'standalone' | 'builtin-group' | 'plugin-group';
  formTitle: string;
  groupId?: string;
  elementType?: Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >;
};

export function getBuiltinGroupConfig(
  groupId: string
): BuiltinWidgetGroupConfig | undefined {
  return BUILTIN_WIDGET_GROUPS[groupId as BuiltinWidgetGroupId];
}

export function getPluginGroupMfes(
  mfes: ApiReturnMfe[],
  pluginName: string
): ApiReturnMfe[] {
  return mfes.filter(
    (mfe) => mfe.type === 'widget' && mfe.plugin_name === pluginName
  );
}

export function parsePluginGroupId(
  widgetGroupId: string
): string | undefined {
  if (!widgetGroupId.startsWith('plugin:')) {
    return undefined;
  }
  return widgetGroupId.slice('plugin:'.length);
}

export function buildWidgetDropdownItems(
  widgets: Record<string, widgetCreationInfo>,
  mfes: ApiReturnMfe[]
): WidgetDropdownItem[] {
  const items: WidgetDropdownItem[] = [];

  if (widgets.plot) {
    items.push({
      kind: 'standalone',
      value: 'plot',
      label: widgets.plot.label,
      formTitle: widgets.plot.formTitle,
      elementType: 'plot',
    });
  }

  for (const [groupId, groupConfig] of Object.entries(BUILTIN_WIDGET_GROUPS)) {
    items.push({
      kind: 'builtin-group',
      value: `group:${groupId}`,
      label: groupConfig.label,
      formTitle: groupConfig.formTitle,
      groupId,
    });
  }

  const pluginNames = [
    ...new Set(
      mfes.filter((mfe) => mfe.type === 'widget').map((mfe) => mfe.plugin_name)
    ),
  ].sort();

  for (const pluginName of pluginNames) {
    items.push({
      kind: 'plugin-group',
      value: `plugin:${pluginName}`,
      label: pluginName,
      formTitle: `Ajouter un élément "${pluginName}"`,
      groupId: `plugin:${pluginName}`,
    });
  }

  return items;
}

export function isGroupedBuiltinWidgetKey(key: string): boolean {
  return GROUPED_BUILTIN_KEYS.has(key);
}
